"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Shield, ChevronRight, ChevronLeft, CircleCheck, Loader, Terminal } from "lucide-react";
const inputStyles = "flex h-11 w-full rounded-md border border-border/50 bg-background/40 px-3 py-2 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 transition-all backdrop-blur-sm";
const textareaStyles = "flex min-h-[120px] w-full rounded-md border border-border/50 bg-background/40 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 transition-all backdrop-blur-sm resize-none";
export default function AccessRequest() {
  return <section data-section-id="235701" id="start-your-project" className="bg-background text-foreground relative min-h-screen overflow-hidden py-24 flex items-center justify-center">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-ring)_0%,transparent_15%)] opacity-10 blur-[100px]" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/cubes.png')] opacity-[0.03]" />
      </div>

      <div className="container relative z-10 max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-accent rounded-lg mb-4 border border-ring/20">
            <Shield className="w-8 h-8 text-ring" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight mb-4 uppercase">
            Start Your <span className="text-ring">Project</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto font-mono text-sm">
            SYSTEM READY... PLEASE PROVIDE PROJECT SPECIFICATIONS FOR AN ACCURATE ELECTRICAL QUOTATION.
          </p>
        </div>

        {}
        <div className="relative rounded-lg p-[1px] overflow-hidden shadow-2xl shadow-ring/10 group">
          {}
          <div className="absolute inset-[-100%] z-0 animate-spin bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0%,transparent_70%,var(--color-ring)_100%)] opacity-70" style={{
          animationDuration: "6s",
          animationTimingFunction: "linear"
        }} />
          
          {}
          <div className="relative z-10 bg-card/80 backdrop-blur-2xl rounded-lg p-6 md:p-10 h-full w-full border border-border/30">
            <FormProvider formKey="start_project_form_contact" formId="start_project_form_contact" sectionName="Access Request" defaultValues={{
            full_name: "",
            email: "",
            discord_id: "",
            steam_id: "",
            access_reason: "",
            agree_rules: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            discord_id: {
              required: true
            },
            steam_id: {
              required: false
            },
            access_reason: {
              required: true
            },
            agree_rules: {
              required: true
            }
          }} submitText="Send Message" successMessage="Project inquiry received. Our lead electrician will review your requirements shortly.">
              <MultiStepForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-accent rounded-lg flex items-center justify-center mb-6 border border-ring/30">
          <CircleCheck className="w-10 h-10 text-ring" />
        </div>
        <h3 className="text-2xl font-bold font-default mb-2 uppercase tracking-wider">Inquiry Sent</h3>
        <p className="text-muted-foreground font-mono text-sm max-w-md">
          {successMessage}
        </p>
        <div className="mt-8 p-4 bg-background/50 rounded-lg border border-border/50 w-full max-w-sm">
          <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-2">
            <Terminal className="w-4 h-4" />
            <span>VOLTAGE_STATUS:</span>
          </div>
          <div className="text-sm font-mono text-ring">
            &gt; DATA_TRANSMITTED
            <br />
            &gt; ENGINEER_ASSIGNED
            <br />
            <span className="animate-pulse">_</span>
          </div>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <div className="space-y-2">
        <div className="flex justify-between text-xs font-mono text-muted-foreground uppercase tracking-wider">
          <span>Phase 0{step}</span>
          <span>0{totalSteps}</span>
        </div>
        <Progress value={step / totalSteps * 100} className="h-1" />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm font-mono flex items-start gap-3">
          <Terminal className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <strong>CIRCUIT_ERROR:</strong> {submitError}
            <br />
            Please check your connections and try again.
          </div>
        </div>}

      <div className="min-h-[320px]">
        {}
        <div className={step === 1 ? "space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" : "hidden"}>
          <div className="mb-6 border-b border-border/50 pb-4">
            <h3 className="text-lg font-semibold font-default text-foreground flex items-center gap-2">
              <span className="text-ring font-mono">01.</span> Client Details
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Provide your primary contact information.</p>
          </div>

          <FormField control={control} name="full_name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Full Name</FormLabel>
                <FormControl>
                  <input {...field} className={inputStyles} placeholder="Enter your name" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Email Address</FormLabel>
                <FormControl>
                  <input type="email" {...field} className={inputStyles} placeholder="email@example.com" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={step === 2 ? "space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" : "hidden"}>
          <div className="mb-6 border-b border-border/50 pb-4">
            <h3 className="text-lg font-semibold font-default text-foreground flex items-center gap-2">
              <span className="text-ring font-mono">02.</span> Site Information
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Help us locate and reach you for the site visit.</p>
          </div>

          <FormField control={control} name="discord_id" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Phone Number</FormLabel>
                <FormControl>
                  <input {...field} className={inputStyles} placeholder="07123 456 789" />
                </FormControl>
                <FormDescription className="text-xs">We will use this to confirm your appointment.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="steam_id" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Service Needed</FormLabel>
                <FormControl>
                  <input {...field} className={inputStyles} placeholder="e.g. EV Charger, Rewiring" />
                </FormControl>
                <FormDescription className="text-xs">Specify the type of electrical work required.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className={step === 3 ? "space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" : "hidden"}>
          <div className="mb-6 border-b border-border/50 pb-4">
            <h3 className="text-lg font-semibold font-default text-foreground flex items-center gap-2">
              <span className="text-ring font-mono">03.</span> Project Scope
            </h3>
            <p className="text-sm text-muted-foreground mt-1">Describe the work and confirm safety protocols.</p>
          </div>

          <FormField control={control} name="access_reason" rules={{
          required: "Project description is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Message / Requirements</FormLabel>
                <FormControl>
                  <textarea {...field} className={textareaStyles} placeholder="Describe the electrical issues or project goals..." />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agree_rules" rules={{
          required: "You must agree to the terms"
        }} render={({
          field
        }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border/50 bg-background/20 p-4">
                <FormControl>
                  <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 h-4 w-4 shrink-0 rounded-sm border-ring bg-background accent-ring focus:ring-ring focus:ring-offset-background" />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-medium cursor-pointer">
                    I agree to the service terms
                  </FormLabel>
                  <FormDescription className="text-xs">
                    Acknowledge our professional service agreement.
                  </FormDescription>
                </div>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="flex items-center justify-between pt-6 border-t border-border/30">
        <Button type="button" variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1 || isSubmitting} className="font-mono uppercase tracking-wider text-xs rounded-md">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {step < totalSteps ? <Button type="button" onClick={() => setStep(s => Math.min(totalSteps, s + 1))} className="font-mono uppercase tracking-wider text-xs rounded-md">
            Next Phase
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button> : <Button type="submit" disabled={isSubmitting} className="font-mono uppercase tracking-wider text-xs bg-ring text-white hover:bg-ring/90 rounded-md">
            {isSubmitting ? <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Transmitting...
              </> : <>
                Send Message
                <Shield className="w-4 h-4 ml-2" />
              </>}
          </Button>}
      </div>
    </Form>;
}